import React, { useEffect, useState } from 'react';
import { getCategories, deleteCategory } from '@/lib/categories';
import type { Category } from '@/types/category';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/use-toast';
import { Edit, Trash2, PlusCircle } from 'lucide-react';

interface CategoriesListProps {
    onEditCategory: (category: Category) => void;
    onAddCategory: () => void;
    refreshKey: number;
}

export function CategoriesList({ onEditCategory, onAddCategory, refreshKey }: CategoriesListProps) {
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchCategoriesData = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getCategories();
            setCategories(data);
        } catch (err) {
            setError('Falha ao carregar categorias.');
            console.error(err);
            toast({ title: 'Erro', description: 'Não foi possível carregar as categorias.', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategoriesData();
    }, [refreshKey]);

    const handleDeleteCategory = async (id: string) => {
        const result = await deleteCategory(id);
        if (result.error) {
            toast({ title: 'Erro', description: `Falha ao excluir categoria: ${result.error}`, variant: 'destructive' });
        } else {
            toast({ title: 'Sucesso', description: 'Categoria marcada como excluída.', variant: 'default' });
            fetchCategoriesData();
        }
    };

    if (loading) {
        return <div>Carregando categorias...</div>;
    }

    if (error) {
        return <div className="text-red-600">{error}</div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-end mb-4">
                 <Button onClick={onAddCategory} size="sm">
                     <PlusCircle className="mr-2 h-4 w-4" /> Nova Categoria
                 </Button>
            </div>
            {categories.length === 0 ? (
                 <p className="text-center text-gray-500">Nenhuma categoria encontrada.</p>
            ) : (
                <div className="border rounded-lg">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Nome</TableHead>
                                {/* <TableHead>Descrição</TableHead> REMOVIDO */}
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Ações</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {categories.map((category) => (
                                <TableRow key={category.id}>
                                    <TableCell className="font-medium">{category.name}</TableCell>
                                    {/* <TableCell>{category.description || '-'}</TableCell> REMOVIDO */}
                                    <TableCell>
                                        <Badge variant={category.status === 'active' ? 'default' : 'outline'} >
                                            {category.status === 'active' ? 'Ativa' : 'Inativa'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button variant="ghost" size="icon" onClick={() => onEditCategory(category)} title="Editar Categoria">
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" title="Excluir Categoria">
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                        Tem certeza que deseja excluir a categoria "{category.name}"? Suas subcategorias associadas também serão marcadas como excluídas.
                                                        Esta ação pode ser reversível (soft delete).
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                    <AlertDialogAction onClick={() => handleDeleteCategory(category.id)} className="bg-red-600 hover:bg-red-700">
                                                        Excluir
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
}
